/**
 * Dropbox als opslag aan of uit. L2 van blok L (Frank, 19 oktober 2026).
 *
 * Staat in het tabblad Data en in het blok Basismap. Uit: NovaMund leest en schrijft
 * niets in een Dropbox-map; kennisbanken daar worden overgeslagen en er wordt niets
 * verwijderd. Alleen een eigenaar kan hem zetten; dat zegt de backend (`mag_zetten`),
 * en de PUT weigert een lid hoe dan ook.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Switch } from "./ui/switch";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

export default function DropboxSchakelaar({ plek }) {
  const { t } = useTaal();
  const [stand, setStand] = useState(null);
  const [fout, setFout] = useState("");

  const laden = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/dropbox`);
      setStand(r?.data || null);
    } catch {
      setStand(null);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const zet = async (aan) => {
    setFout("");
    try {
      const r = await axios.put(`${API}/settings/dropbox`, { aan });
      setStand((s) => ({ ...s, aan: r?.data?.aan }));
    } catch (e) {
      setFout(e?.response?.status === 403
        ? t("Alleen een eigenaar kan Dropbox aan of uit zetten.")
        : t("Dropbox aan of uit zetten is mislukt."));
    }
  };

  if (!stand) return null;
  return (
    <div className="flex items-start justify-between gap-3" data-testid={`dropbox-schakelaar-${plek}`}>
      <div className="space-y-1">
        <p className="text-sm font-medium">{t("Dropbox als opslag")}</p>
        <p className="text-xs text-muted-foreground" data-testid="dropbox-uitleg">
          {stand.aan
            ? t("Aan: NovaMund leest en schrijft in je Dropbox-mappen.")
            : t("Uit: NovaMund leest en schrijft niets in een Dropbox-map. Kennisbanken daar worden overgeslagen; er wordt niets verwijderd.")}
        </p>
        {!stand.mag_zetten && (
          <p className="text-xs text-muted-foreground">{t("Alleen een eigenaar kan Dropbox aan of uit zetten.")}</p>
        )}
        {fout && <p className="text-xs text-destructive" role="alert">{fout}</p>}
      </div>
      <Switch
        checked={stand.aan === true}
        onCheckedChange={zet}
        disabled={!stand.mag_zetten}
        aria-label={t("Dropbox als opslag")}
        data-testid={`dropbox-switch-${plek}`}
        className="relative flex-shrink-0 after:absolute after:-inset-3 after:content-['']"
      />
    </div>
  );
}
